import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Upload, Plus, X, ChevronLeft, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useApp } from "@/contexts/AppContext";
import Header from "@/components/Header";
import AdBanner from "@/components/AdBanner";
import PageAdLayout from "@/components/PageAdLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ['scan', 'people', 'assign', 'results'];
const COLORS = [
  'bg-blue-100 text-blue-700', 'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700', 'bg-rose-100 text-rose-700',
  'bg-purple-100 text-purple-700', 'bg-cyan-100 text-cyan-700',
];

const SCAN_STAGES = [
  { en: "Uploading image...", id: "Mengunggah gambar..." },
  { en: "Reading text from receipt...", id: "Membaca teks dari struk..." },
  { en: "Identifying items & prices...", id: "Mengenali item & harga..." },
  { en: "Calculating totals...", id: "Menghitung total..." },
];

const useScanStage = (active) => {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    if (!active) { setStage(0); return; }
    const timers = [3000, 7000, 12000].map((ms, i) =>
      setTimeout(() => setStage(i + 1), ms)
    );
    return () => timers.forEach(clearTimeout);
  }, [active]);
  return Math.min(stage, SCAN_STAGES.length - 1);
};

const Avatar = ({ name, index }) => (
  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${COLORS[index % COLORS.length]}`}>
    {name?.trim() ? name.trim()[0].toUpperCase() : index + 1}
  </div>
);

const LandingPage = () => {
  const { t, formatAmount, currency, setCurrency, language } = useApp();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  // Step state
  const [step, setStep] = useState('scan');
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [billData, setBillData] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualAmount, setManualAmount] = useState('');
  const [manualDesc, setManualDesc] = useState('');

  // People step
  const [people, setPeople] = useState(['', '']);
  const [paidByIndex, setPaidByIndex] = useState(0);

  // Item assignment: { [itemIndex]: Set<name> }
  const [assignments, setAssignments] = useState({});

  // Save state
  const [saving, setSaving] = useState(false);

  // Join group
  const [joinId, setJoinId] = useState('');

  const scanStage = useScanStage(scanning);

  const totalAmount = billData?.total || parseFloat(manualAmount) || 0;
  const billTitle = billData?.title || manualDesc || t('my_bill');
  const validPeople = people.filter(p => p.trim());
  const safePaidByIndex = Math.min(paidByIndex, validPeople.length - 1);
  const paidByName = validPeople[safePaidByIndex];
  const currentStep = STEPS.indexOf(step);

  // Per-person share: item cost split among assigned people (default: everyone).
  // Tax/service (is_tax) lines auto-split proportional to each person's food subtotal.
  const personTotals = (() => {
    const totals = Object.fromEntries(validPeople.map(n => [n, 0]));
    const items = billData?.items || [];
    if (items.length === 0) {
      // No itemization (manual entry or blank scan) → equal split
      const share = validPeople.length > 0 ? totalAmount / validPeople.length : 0;
      validPeople.forEach(n => totals[n] += share);
      return totals;
    }
    let taxTotal = 0, foodTotals = { ...totals };
    items.forEach((it, i) => {
      const amt = Number(it.amount || 0);
      if (it.is_tax) { taxTotal += amt; return; }
      const assigned = assignments[i];
      const who = (assigned && assigned.size > 0)
        ? [...assigned].filter(n => validPeople.includes(n))
        : validPeople; // default: shared by everyone
      const share = who.length > 0 ? amt / who.length : 0;
      who.forEach(n => { totals[n] += share; foodTotals[n] += share; });
    });
    const foodSum = Object.values(foodTotals).reduce((a, b) => a + b, 0) || 1;
    validPeople.forEach(n => { totals[n] += taxTotal * (foodTotals[n] / foodSum); });
    return totals;
  })();
  const personTotalList = validPeople.map(n => ({ name: n, amount: personTotals[n] || 0 }));

    const processImage = async (file) => {
    setScanning(true);
    setScanError('');
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const base64 = reader.result.split(',')[1];
        const res = await axios.post(`${API}/scan-bill`, { image_base64: base64, currency });
        setBillData(res.data);
        setStep('people');
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (detail === 'INSUFFICIENT_BALANCE') {
          setScanError(t('scan_error_balance'));
        } else {
          setScanError(t('scan_error'));
        }
      } finally {
        setScanning(false);
      }
    };
    reader.onerror = () => { setScanError(t('scan_error')); setScanning(false); };
    reader.readAsDataURL(file);
  };

  const handleManualContinue = () => {
    if (!manualAmount || parseFloat(manualAmount) <= 0) { toast.error(t('error_amount')); return; }
    setBillData({ title: manualDesc || t('my_bill'), items: [], total: parseFloat(manualAmount) });
    setStep('people');
  };

  const handleCalculate = () => {
    if (validPeople.length < 2) { toast.error(t('error_min_people')); return; }
    setStep(billData?.items?.length > 0 ? 'assign' : 'results');
  };

  const toggleItemPerson = (itemIdx, name) => {
    setAssignments(prev => {
      const cur = new Set(prev[itemIdx] || []);
      if (cur.has(name)) cur.delete(name); else cur.add(name);
      return { ...prev, [itemIdx]: cur };
    });
  };

  const handleSaveAndShare = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/groups`, { name: billTitle, currency, members: validPeople });
      const group = res.data;
      const nameToId = Object.fromEntries(group.members.map(m => [m.name, m.id]));
      const paidById = nameToId[paidByName] || group.members[0].id;
      const date = new Date().toISOString().split('T')[0];
      const payers = personTotalList.filter(p => p.amount > 0.01);
      // Single expense with per-member shares — backend stores shares and
      // calculate_balances honors them (no more N fake equal-split expenses).
      const shares = Object.fromEntries(payers.map(p => [nameToId[p.name], Math.round(p.amount)]));
      await axios.post(`${API}/groups/${group.id}/expenses`, {
        description: billTitle,
        amount: totalAmount,
        paid_by: paidById,
        participants: payers.map(p => nameToId[p.name]),
        shares,
        date
      });
      // Persist full itemized bill for group view summary
      if (billData?.items?.length > 0) {
        await axios.post(`${API}/groups/${group.id}/bills`, {
          title: billTitle,
          items: billData.items,
          total: totalAmount,
          currency
        });
      }
      navigate(`/group/${group.id}`);
    } catch {
      toast.error(t('error'));
      setSaving(false);
    }
  };

  const resetFlow = () => {
    setStep('scan'); setBillData(null); setManualAmount(''); setManualDesc('');
    setScanError(''); setManualMode(false); setPeople(['', '']); setPaidByIndex(0); setAssignments({});
  };

  const handleJoin = () => {
    if (!joinId.trim()) return;
    const id = joinId.includes('/group/') ? joinId.split('/group/')[1].split('?')[0].trim() : joinId.trim();
    navigate(`/group/${id}`);
  };

  const updatePerson = (i, v) => setPeople(prev => prev.map((n, idx) => idx === i ? v : n));
  const removePerson = (i) => { setPeople(prev => prev.filter((_, idx) => idx !== i)); if (paidByIndex >= i && paidByIndex > 0) setPaidByIndex(p => p - 1); };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <PageAdLayout topSection={
        /* Progress Bar — full width */
        <div className="bg-white border-b border-zinc-100">
          <div className="max-w-lg mx-auto px-4 py-3">
            <div className="flex gap-1.5">
              {STEPS.map((s, i) => (
                <div key={s} className={`h-1 flex-1 rounded-full transition-all duration-400 ${i <= currentStep ? 'bg-zinc-900' : 'bg-zinc-100'}`} />
              ))}
            </div>
            <div className="flex justify-between mt-1.5">
              {STEPS.map((s, i) => (
                <span key={s} className={`text-xs font-medium transition-colors ${i === currentStep ? 'text-zinc-900' : i < currentStep ? 'text-zinc-400' : 'text-zinc-200'}`}>
                  {i < currentStep ? <Check size={11} className="inline -mt-0.5" /> : null} {t(`step_${s}`)}
                </span>
              ))}
            </div>
          </div>
        </div>
      }>
        {/* Main Content */}
        <div className="max-w-lg mx-auto w-full px-4 py-8">
        <AnimatePresence mode="wait">

          {/* ── STEP 1A: SCAN ── */}
          {step === 'scan' && !scanning && !manualMode && (
            <motion.div key="scan" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.25 }}>
              <h1 className="text-3xl sm:text-4xl font-bold text-zinc-900 font-outfit tracking-tight mb-2">
                {t('scan_your_bill')}
              </h1>
              <p className="text-zinc-400 text-base mb-6">{t('scan_hero_subtitle')}</p>

              {/* Currency quick pick */}
              <div className="flex items-center gap-2 mb-6">
                <span className="text-sm text-zinc-500">{t('currency')}:</span>
                {['IDR', 'USD'].map(c => (
                  <button key={c} onClick={() => setCurrency(c)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${currency === c ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-200 text-zinc-500 hover:border-zinc-400'}`}>
                    {c}
                  </button>
                ))}
              </div>

              {/* Scan Area */}
              <div className="border-2 border-dashed border-zinc-200 rounded-2xl p-10 text-center bg-zinc-50 mb-3">
                <Camera size={52} className="mx-auto text-zinc-300 mb-4" strokeWidth={1.5} />
                <p className="text-sm text-zinc-400 mb-6">{t('scan_bill_desc')}</p>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={e => e.target.files[0] && processImage(e.target.files[0])}
                  className="hidden"
                  data-testid="camera-upload-input"
                />
                <div className="flex flex-col gap-3 max-w-xs mx-auto">
                  <Button
                    onClick={() => { fileRef.current.setAttribute('capture', 'camera'); fileRef.current.click(); }}
                    className="h-12 bg-zinc-900 hover:bg-zinc-800 text-white"
                    data-testid="take-photo-btn"
                  >
                    <Camera size={16} strokeWidth={1.5} className="mr-2" /> {t('take_photo')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { fileRef.current.removeAttribute('capture'); fileRef.current.click(); }}
                    className="h-12 border-zinc-200"
                    data-testid="upload-file-btn"
                  >
                    <Upload size={16} strokeWidth={1.5} className="mr-2" /> {t('upload_file')}
                  </Button>
                </div>
                {scanError && <p className="text-rose-600 text-sm mt-4">{scanError}</p>}
              </div>

              <button
                onClick={() => setManualMode(true)}
                className="w-full text-center text-sm text-zinc-400 hover:text-zinc-700 transition-colors py-1.5"
                data-testid="enter-manually-btn"
              >
                {t('enter_manually')}
              </button>

              {/* Join group secondary */}
              <div className="mt-8 pt-6 border-t border-zinc-100">
                <p className="text-xs text-zinc-400 uppercase tracking-[0.15em] font-semibold mb-3">{t('or_join_group')}</p>
                <div className="flex gap-2">
                  <Input
                    data-testid="join-group-input"
                    placeholder={t('enter_group_id')}
                    value={joinId}
                    onChange={e => setJoinId(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleJoin()}
                    className="h-10 text-sm"
                  />
                  <Button onClick={handleJoin} variant="outline" className="h-10 shrink-0 border-zinc-200" data-testid="join-group-btn">
                    {t('join')}
                  </Button>
                </div>
              </div>

              {/* Ad banner - scan step */}
              {/* (Ads now provided by PageAdLayout on all sides) */}
            </motion.div>
          )}

          {/* ── STEP 1B: MANUAL ── */}
          {step === 'scan' && !scanning && manualMode && (
            <motion.div key="manual" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.25 }}>
              <h1 className="text-3xl font-bold text-zinc-900 font-outfit tracking-tight mb-2">{t('enter_manually')}</h1>
              <p className="text-zinc-400 mb-8">{t('scan_hero_subtitle')}</p>

              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-semibold">{t('manual_amount')} ({currency})</Label>
                  <Input
                    data-testid="manual-amount-input"
                    type="number" min="0"
                    value={manualAmount}
                    onChange={e => setManualAmount(e.target.value)}
                    placeholder="0"
                    className="mt-1.5 h-12 text-lg font-semibold"
                    autoFocus
                    onKeyDown={e => e.key === 'Enter' && handleManualContinue()}
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-semibold">{t('manual_description')}</Label>
                  <Input
                    value={manualDesc}
                    onChange={e => setManualDesc(e.target.value)}
                    placeholder={t('expense_description_placeholder')}
                    className="mt-1.5 h-11"
                    onKeyDown={e => e.key === 'Enter' && handleManualContinue()}
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setManualMode(false)} className="h-12 border-zinc-200">
                    <ChevronLeft size={16} />
                  </Button>
                  <Button onClick={handleManualContinue} className="flex-1 h-12 bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="manual-continue-btn">
                    {t('continue')} <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* ── SCANNING SPINNER ── */}
          {step === 'scan' && scanning && (
            <motion.div key="scanning" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-28 text-center">
              <div className="relative w-16 h-16 mx-auto mb-6">
                <div className="absolute inset-0 border-4 border-zinc-100 rounded-full" />
                <div className="absolute inset-0 border-4 border-transparent border-t-zinc-900 rounded-full animate-spin" />
              </div>
              <p className="text-xl font-semibold text-zinc-900">{t('scanning')}</p>
              <motion.p
                key={scanStage}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="text-zinc-400 text-sm mt-1"
              >
                {SCAN_STAGES[scanStage][language] || SCAN_STAGES[scanStage].en}
              </motion.p>
              <div className="flex justify-center gap-1.5 mt-5">
                {SCAN_STAGES.map((_, i) => (
                  <div key={i} className={`w-2 h-2 rounded-full transition-colors duration-300 ${i <= scanStage ? 'bg-zinc-900' : 'bg-zinc-200'}`} />
                ))}
              </div>
            </motion.div>
          )}

          {/* ── STEP 2: PEOPLE ── */}
          {step === 'people' && (
            <motion.div key="people" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }}>
              {/* Bill summary pill */}
              <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-4 mb-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-semibold mb-0.5">{t('scan_bill')}</p>
                    <p className="font-semibold text-zinc-900">{billTitle}</p>
                  </div>
                  <p className="text-2xl font-bold text-zinc-900">{formatAmount(totalAmount)}</p>
                </div>
                {billData?.items?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-zinc-200 space-y-1">
                    {billData.items.map((item, i) => (
                      <div key={i} className="flex justify-between text-xs text-zinc-500">
                        <span className="flex items-center gap-1.5">
                          {item.is_tax && (
                            <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">{t('tax')}</span>
                          )}
                          {item.name}
                        </span>
                        <span>{Number(item.amount).toLocaleString('id-ID')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <h2 className="text-2xl font-bold text-zinc-900 font-outfit mb-4">{t('whos_splitting')}</h2>

              {/* People list */}
              <div className="space-y-2 mb-5">
                {people.map((p, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Avatar name={p} index={i} />
                    <Input
                      data-testid={`person-name-input-${i}`}
                      placeholder={`${t('person_name')} ${i + 1}`}
                      value={p}
                      onChange={e => updatePerson(i, e.target.value)}
                      className="h-10 flex-1"
                    />
                    {people.length > 2 && (
                      <button onClick={() => removePerson(i)} className="p-1.5 text-zinc-300 hover:text-rose-500 transition-colors">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => setPeople(prev => [...prev, ''])}
                  className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-700 transition-colors ml-9"
                >
                  <Plus size={13} /> {t('add_person')}
                </button>
              </div>

              {/* Who paid */}
              <div className="mb-6">
                <p className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-semibold mb-2">{t('whos_paying')}</p>
                <div className="flex flex-wrap gap-2">
                  {people.map((p, i) => p.trim() ? (
                    <button
                      key={i}
                      data-testid={`paid-by-btn-${i}`}
                      onClick={() => setPaidByIndex(i)}
                      className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-all ${paidByIndex === i ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-200 text-zinc-600 hover:border-zinc-400'}`}
                    >
                      {p.trim()}
                    </button>
                  ) : null)}
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setStep('scan'); setScanError(''); }} className="h-12 border-zinc-200 px-4">
                  <ChevronLeft size={16} />
                </Button>
                <Button
                  data-testid="calculate-btn"
                  onClick={handleCalculate}
                  className="flex-1 h-12 bg-zinc-900 hover:bg-zinc-800 text-white font-medium"
                >
                  {t('calculate')} <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* ── STEP 2.5: ASSIGN ITEMS ── */}
          {step === 'assign' && (
            <motion.div key="assign" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }}>
              <h2 className="text-2xl font-bold text-zinc-900 font-outfit mb-1">{t('step_assign')}</h2>
              <p className="text-sm text-zinc-400 mb-5">{t('assign_hint')}</p>

              <div className="space-y-2.5 mb-6">
                {billData.items.map((item, i) => (
                  <div key={i} className="border border-zinc-200 rounded-xl p-3.5">
                    <div className="flex justify-between items-center mb-2.5">
                      <span className="text-sm font-medium text-zinc-900 flex items-center gap-1.5">
                        {item.is_tax && (
                          <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">{t('tax')}</span>
                        )}
                        {item.name}
                      </span>
                      <span className="text-sm font-semibold text-zinc-900">{Number(item.amount).toLocaleString('id-ID')}</span>
                    </div>
                    {item.is_tax ? (
                      <p className="text-xs text-zinc-400 italic">{t('assign_hint')}</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {validPeople.map((name) => {
                          const assigned = assignments[i];
                          const on = !assigned || assigned.size === 0 ? true : assigned.has(name);
                          return (
                            <button
                              key={name}
                              onClick={() => toggleItemPerson(i, name)}
                              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${on ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-200 text-zinc-400 hover:border-zinc-400'}`}
                            >
                              {name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Live per-person totals */}
              <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-4 mb-6">
                <div className="space-y-2">
                  {personTotalList.map((p, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Avatar name={p.name} index={i} />
                        <span className="text-sm font-medium text-zinc-900">{p.name}</span>
                      </div>
                      <span className="text-sm font-bold text-zinc-900">{formatAmount(p.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep('people')} className="h-12 border-zinc-200 px-4">
                  <ChevronLeft size={16} />
                </Button>
                <Button
                  onClick={() => setStep('results')}
                  className="flex-1 h-12 bg-zinc-900 hover:bg-zinc-800 text-white font-medium"
                >
                  {t('calculate')} <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* ── STEP 3: RESULTS ── */}
          {step === 'results' && (
            <motion.div key="results" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }}>
              <h2 className="text-3xl font-bold text-zinc-900 font-outfit tracking-tight mb-6">{t('split_results')}</h2>

              {/* Summary Card */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-5 mb-4 shadow-sm">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="font-semibold text-zinc-900">{billTitle}</p>
                    <p className="text-sm text-zinc-400">{validPeople.length} {t('people')} · {formatAmount(totalAmount)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-zinc-400 mb-0.5">{t('paid_by')}</p>
                    <p className="text-sm font-bold text-zinc-900">{paidByName}</p>
                  </div>
                </div>

                {/* Itemized breakdown */}
                {billData?.items?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-zinc-100 space-y-2">
                    {billData.items.map((item, i) => {
                      const amt = Number(item.amount || 0);
                      const assigned = assignments[i];
                      const who = item.is_tax ? null
                        : (assigned && assigned.size > 0 ? [...assigned].filter(n => validPeople.includes(n)) : validPeople);
                      const share = who && who.length > 0 ? amt / who.length : 0;
                      return (
                        <div key={i} className="text-xs">
                          <div className="flex justify-between mb-1">
                            <span className="flex items-center gap-1.5 text-zinc-700">
                              {item.is_tax && (
                                <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">{t('tax')}</span>
                              )}
                              {item.name}
                            </span>
                            <span className="text-zinc-500">{Number(amt).toLocaleString('id-ID')}</span>
                          </div>
                          {item.is_tax ? (
                            <p className="text-[11px] text-zinc-400 pl-1">{t('assign_hint')}</p>
                          ) : (
                            <div className="flex flex-wrap gap-x-3 pl-1">
                              {who.map(n => (
                                <span key={n} className="text-[11px] text-zinc-500">
                                  {n} <span className="text-zinc-400">{Number(share).toLocaleString('id-ID')}</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="space-y-2.5 pt-4 border-t border-zinc-100 mt-4">
                  {personTotalList.map((p, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Avatar name={p.name} index={i} />
                        <span className="text-sm font-medium text-zinc-900">{p.name}{p.name === paidByName && <span className="text-zinc-400 ml-1">· {t('paid')}</span>}</span>
                      </div>
                      <span className="text-sm font-bold text-zinc-900">{formatAmount(p.amount)}</span>
                    </div>
                  ))}
                </div>

                {/* Pembulatan reconciliation: show any delta between the split
                    sum and the printed total (rounding line / unmatched items). */}
                {(() => {
                  const splitSum = personTotalList.reduce((s, p) => s + p.amount, 0);
                  const delta = Math.round(totalAmount) - Math.round(splitSum);
                  if (Math.abs(delta) < 1) return null;
                  return (
                    <div className="mt-3 pt-3 border-t border-zinc-100 flex justify-between text-[11px] text-zinc-400">
                      <span>{t('rounding_note')}</span>
                      <span>{delta > 0 ? '+' : ''}{Number(delta).toLocaleString('id-ID')}</span>
                    </div>
                  );
                })()}
              </div>

              {/* Settlements — each non-payer owes their share to whoever paid */}
              {validPeople.length > 1 && (
                <div className="mb-6 space-y-2">
                  <p className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-semibold">{t('suggested_settlements')}</p>
                  {personTotalList.filter(p => p.name !== paidByName).map((p, i) => (
                    <div key={i} className="flex items-center justify-between p-3.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm">
                      <div>
                        <span className="font-semibold text-zinc-900">{p.name}</span>
                        <span className="text-zinc-400 mx-2">{t('pays')}</span>
                        <span className="font-semibold text-zinc-900">{paidByName}</span>
                      </div>
                      <span className="font-bold text-zinc-900">{formatAmount(p.amount)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Ad banner - results step */}
              {/* (Ads now provided by PageAdLayout on all sides) */}

              <div className="flex gap-3">
                <Button variant="outline" onClick={resetFlow} className="h-12 flex-1 border-zinc-200">
                  {t('split_again')}
                </Button>
                <Button
                  data-testid="save-and-share-btn"
                  onClick={handleSaveAndShare}
                  disabled={saving}
                  className="flex-1 h-12 bg-zinc-900 hover:bg-zinc-800 text-white font-medium"
                >
                  {saving ? t('creating') : t('save_and_share')}
                  {!saving && <ArrowRight size={16} className="ml-2" />}
                </Button>
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
      </PageAdLayout>
    </div>
  );
};

export default LandingPage;
